// Notes are Markdown, written as text and shown formatted, as in the apps: paragraphs, headings, lists, **bold**,
// *italic*, `code`, ~~struck~~ and links. Built from React elements, never as HTML, so nothing in a note can run.

import type { ReactNode } from "react";

const INLINE = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*\s][^*]*\*|_[^_\s][^_]*_|`[^`]+`|~~[^~]+~~|\[[^\]]+\]\([^)\s]+\)|https?:\/\/[^\s)]+)/g;

function safeLink(href: string): string | undefined {
  return /^(https?:|mailto:)/i.test(href) ? href : undefined;
}

function inline(text: string, key: string): ReactNode[] {
  const parts: ReactNode[] = [];
  let last = 0;
  let index = 0;
  for (const match of text.matchAll(INLINE)) {
    const token = match[0];
    const start = match.index ?? 0;
    if (start > last) parts.push(text.slice(last, start));
    const id = `${key}-${index++}`;
    if (token.startsWith("**") || token.startsWith("__")) parts.push(<strong key={id}>{inline(token.slice(2, -2), id)}</strong>);
    else if (token.startsWith("~~")) parts.push(<s key={id}>{token.slice(2, -2)}</s>);
    else if (token.startsWith("`")) parts.push(<code key={id}>{token.slice(1, -1)}</code>);
    else if (token.startsWith("[")) {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      const href = link ? safeLink(link[2] ?? "") : undefined;
      parts.push(href ? <a key={id} href={href} target="_blank" rel="noopener noreferrer">{link?.[1]}</a> : token);
    } else if (/^https?:/.test(token)) parts.push(<a key={id} href={token} target="_blank" rel="noopener noreferrer">{token}</a>);
    else parts.push(<em key={id}>{inline(token.slice(1, -1), id)}</em>);
    last = start + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  const lines = text.split("\n");
  let list: { ordered: boolean; items: string[] } | null = null;
  let paragraph: string[] = [];
  const flush = () => {
    if (paragraph.length) {
      const key = `p${blocks.length}`;
      blocks.push(<p key={key}>{paragraph.flatMap((line, index) => (index ? [<br key={`${key}-br${index}`} />, ...inline(line, `${key}-${index}`)] : inline(line, `${key}-${index}`)))}</p>);
      paragraph = [];
    }
    if (list) {
      const key = `l${blocks.length}`;
      const items = list.items.map((item, index) => <li key={index}>{inline(item, `${key}-${index}`)}</li>);
      blocks.push(list.ordered ? <ol key={key}>{items}</ol> : <ul key={key}>{items}</ul>);
      list = null;
    }
  };
  for (const line of lines) {
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (line.trim() === "") flush();
    else if (heading) {
      flush();
      blocks.push(<h4 key={`h${blocks.length}`}>{inline(heading[2] ?? "", `h${blocks.length}`)}</h4>);
    } else if (bullet || numbered) {
      if (paragraph.length) flush();
      const ordered = numbered !== null;
      if (list && list.ordered !== ordered) flush();
      list ??= { ordered, items: [] };
      list.items.push((bullet ?? numbered)?.[1] ?? "");
    } else {
      if (list) flush();
      paragraph.push(line);
    }
  }
  flush();
  return <div className="markdown">{blocks}</div>;
}
